import React, { forwardRef, useEffect, useMemo, useState } from 'react';
import { DEFAULT_PAGE_SIZE } from '@/__core/constants/pagination';
import {
  Typography,
  Chip,
  Box,
  IconButton,
  CircularProgress,
} from '@mui/material';
import { DragIndicator as DragIndicatorIcon, ContentCopy as ContentCopyIcon } from '@mui/icons-material';
import { NumericFormat } from 'react-number-format';
import { arrayMove } from '@dnd-kit/sortable';
import { AppPagination, EmployeeDisplay, EmployeeAvatarGroup } from '@/__core/components';
import DataTableModule from '@/__core/layout/module/DataTableModule';
import { sharedListStyles } from '@/__core/styles/sharedListStyles';
import { useOrderProgressPermissions } from '../hooks/useOrderProgressPermissions';
import type { OrderProgressItem } from '../models/OrderProgressModel';
import { formatDateHeader, getDateKey, isTodayDate } from '../utils/orderProgressDateUtils';
import EditProgressModal from './EditProgressModal';

export type MainListOrderProgressHandle = Record<string, never>;

interface MainListOrderProgressProps {
  progressItems: OrderProgressItem[];
  loading?: boolean;
  onRowClick?: (item: OrderProgressItem) => void;
  onCopyClick?: (item: OrderProgressItem) => void;
  onUpdate?: () => void;
  onReorderItems?: (dateKey: string, newOrder: OrderProgressItem[]) => void;
  onSuccess?: (message: string) => void;
  canCreate?: boolean;
  canUpdate?: boolean;
}

type ProgressGroupRow = {
  id: string;
  __fullWidthRow: true;
  __hideCheckbox: true;
  dateKey: string;
  date: string;
  count: number;
  isToday: boolean;
};

type ProgressItemRow = OrderProgressItem & {
  __groupIndex: number;
  __dateKey: string;
};

type ProgressDataRow = ProgressGroupRow | ProgressItemRow;

const isGroupRow = (row: ProgressDataRow): row is ProgressGroupRow => {
  return (row as ProgressGroupRow).__fullWidthRow === true;
};

const MainListOrderProgress = forwardRef<MainListOrderProgressHandle, MainListOrderProgressProps>(
  function MainListOrderProgress(
    {
      progressItems,
      loading = false,
      onRowClick,
      onCopyClick,
      onUpdate,
      onReorderItems,
      onSuccess,
      canCreate: canCreateProp,
      canUpdate: canUpdateProp,
    },
    _ref,
  ) {
  const { canCreate: hasCreatePermission, canUpdate: hasUpdatePermission } = useOrderProgressPermissions();
  const canCreate = canCreateProp ?? hasCreatePermission;
  const canUpdate = canUpdateProp ?? hasUpdatePermission;
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<OrderProgressItem | null>(null);
  const [customOrder, setCustomOrder] = useState<Map<string, string[]>>(new Map());
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);

  const groupedItems = useMemo(() => {
    const groups = new Map<string, OrderProgressItem[]>();

    progressItems.forEach((item) => {
      const dateKey = getDateKey(item.date);
      if (!dateKey) return;

      if (!groups.has(dateKey)) {
        groups.set(dateKey, []);
      }

      groups.get(dateKey)!.push(item);
    });

    return Array.from(groups.entries())
      .map(([dateKey, items]) => {
        const orderForDate = customOrder.get(dateKey);
        let sortedItems: OrderProgressItem[];

        const allHaveOrderIndex = items.every((item) => item.order_index !== undefined && item.order_index !== null);

        if (allHaveOrderIndex) {
          sortedItems = [...items].sort((a, b) => {
            const indexA = a.order_index ?? 999999;
            const indexB = b.order_index ?? 999999;
            if (indexA !== indexB) {
              return indexA - indexB;
            }
            const timeA = a.created_at ? new Date(a.created_at).getTime() : 0;
            const timeB = b.created_at ? new Date(b.created_at).getTime() : 0;
            return timeB - timeA;
          });
        } else if (orderForDate && orderForDate.length > 0) {
          const itemMap = new Map(items.map((item) => [item.id, item]));
          sortedItems = orderForDate
            .map((id) => itemMap.get(id))
            .filter((item): item is OrderProgressItem => item !== undefined);

          const orderedIds = new Set(orderForDate);
          const unorderedItems = items.filter((item) => !orderedIds.has(item.id));
          sortedItems = [...sortedItems, ...unorderedItems];
        } else {
          sortedItems = [...items].sort((a, b) => {
            const timeA = a.created_at ? new Date(a.created_at).getTime() : 0;
            const timeB = b.created_at ? new Date(b.created_at).getTime() : 0;
            return timeB - timeA;
          });
        }

        return {
          dateKey,
          date: items[0]?.date || '',
          items: sortedItems,
          count: items.length,
        };
      })
      .sort((a, b) => {
        const dateA = a.date ? new Date(a.date).getTime() : 0;
        const dateB = b.date ? new Date(b.date).getTime() : 0;
        return dateB - dateA;
      });
  }, [progressItems, customOrder]);

  const pagedGroupedItems = useMemo(() => {
    const flattenedItems = groupedItems.flatMap((group) =>
      group.items.map((item) => ({
        ...item,
        __dateKey: group.dateKey,
        __date: group.date,
      }))
    );

    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const pagedItems = flattenedItems.slice(startIndex, endIndex);

    const groupedMap = new Map<string, { date: string; items: OrderProgressItem[] }>();

    pagedItems.forEach((item) => {
      if (!groupedMap.has(item.__dateKey)) {
        groupedMap.set(item.__dateKey, {
          date: item.__date,
          items: [],
        });
      }

      groupedMap.get(item.__dateKey)!.items.push(item);
    });

    return Array.from(groupedMap.entries()).map(([dateKey, group]) => ({
      dateKey,
      date: group.date,
      items: group.items,
      count: group.items.length,
    }));
  }, [groupedItems, currentPage, pageSize]);

  const totalCount = progressItems.length;
  const totalPages = Math.max(Math.ceil(totalCount / pageSize), 1);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const tableRows = useMemo<ProgressDataRow[]>(() => {
    const rows: ProgressDataRow[] = [];

    pagedGroupedItems.forEach((group) => {
      rows.push({
        id: `group-${group.dateKey}`,
        __fullWidthRow: true,
        __hideCheckbox: true,
        dateKey: group.dateKey,
        date: group.date,
        count: group.count,
        isToday: isTodayDate(group.date),
      });

      group.items.forEach((item, index) => {
        rows.push({
          ...item,
          __groupIndex: index,
          __dateKey: group.dateKey,
        });
      });
    });

    return rows;
  }, [pagedGroupedItems]);

  const rowsById = useMemo(() => {
    const map = new Map<string, ProgressDataRow>();
    tableRows.forEach((row) => map.set(row.id, row));
    return map;
  }, [tableRows]);

  const handleRowClick = (item: OrderProgressItem) => {
    if (onRowClick) {
      onRowClick(item);
      return;
    }

    if (!canUpdate) {
      return;
    }

    setSelectedItem(item);
    setEditModalOpen(true);
  };

  const handleRowReorder = (activeRowId: string, overRowId: string) => {
    if (!canUpdate || !onReorderItems || activeRowId === overRowId) return;

    const activeRow = rowsById.get(activeRowId);
    const overRow = rowsById.get(overRowId);

    if (!activeRow || !overRow || isGroupRow(activeRow) || isGroupRow(overRow)) return;
    if (activeRow.__dateKey !== overRow.__dateKey) return;

    const group = groupedItems.find((item) => item.dateKey === activeRow.__dateKey);
    if (!group) return;

    const oldIndex = group.items.findIndex((item) => item.id === activeRow.id);
    const newIndex = group.items.findIndex((item) => item.id === overRow.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const newOrder = arrayMove(group.items, oldIndex, newIndex);

    setCustomOrder((prev) => {
      const next = new Map(prev);
      next.set(group.dateKey, newOrder.map((item) => item.id));
      return next;
    });

    onReorderItems(group.dateKey, newOrder);
  };

  const columns = [
    {
      id: 'stt',
      label: 'STT',
      width: '52px',
      align: 'center' as const,
      headerAlign: 'center' as const,
      render: (_value: any, row: ProgressDataRow) => {
        if (isGroupRow(row)) {
          return (
            <Typography
              variant="body2"
              sx={{
                fontWeight: 700,
                fontSize: '0.875rem',
                color: row.isToday ? '#1d4ed8' : '#111827',
              }}
            >
              {formatDateHeader(row.date)} ({row.count})
            </Typography>
          );
        }

        return (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0,
              width: '100%',
            }}
          >
            <IconButton
              size="small"
              data-row-drag-handle="true"
              onClick={(event) => event.stopPropagation()}
              sx={{
                color: '#9ca3af',
                cursor: 'grab',
                p: 0,
                width: 22,
                height: 22,
                '&:active': { cursor: 'grabbing' },
              }}
            >
              <DragIndicatorIcon sx={{ fontSize: 16 }} />
            </IconButton>
            <Typography
              variant="body2"
              sx={{
                fontSize: '0.8125rem',
                color: '#6b7280',
                lineHeight: 1,
                minWidth: 14,
                textAlign: 'center',
              }}
            >
              {row.__groupIndex + 1}
            </Typography>
          </Box>
        );
      },
    },
    {
      id: 'order_code',
      label: 'Mã đơn hàng',
      width: '150px',
      render: (_value: any, row: ProgressDataRow) => {
        if (isGroupRow(row)) return null;
        return (
          <Typography
            variant="body2"
            sx={{
              color: '#111827',
              fontSize: '0.875rem',
              fontFamily: 'monospace',
            }}
          >
            {row.order_code || 'N/A'}
          </Typography>
        );
      },
    },
    {
      id: 'customer_name',
      label: 'Khách hàng',
      width: '200px',
      render: (_value: any, row: ProgressDataRow) => {
        if (isGroupRow(row)) return null;
        return (
          <Typography
            variant="body2"
            sx={{
              fontSize: '0.875rem',
              color: '#111827',
              fontWeight: 500,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: '100%',
            }}
            title={row.customer_name || 'N/A'}
          >
            {row.customer_name || 'N/A'}
          </Typography>
        );
      },
    },
    {
      id: 'phone',
      label: 'SĐT',
      width: '130px',
      render: (_value: any, row: ProgressDataRow) => {
        if (isGroupRow(row)) return null;
        return (
          <Typography
            variant="body2"
            sx={{
              fontSize: '0.875rem',
              color: '#374151',
              fontFamily: 'monospace',
            }}
          >
            {row.phone || '-'}
          </Typography>
        );
      },
    },
    {
      id: 'delivery_address_line',
      label: 'Địa chỉ giao hàng',
      width: '300px',
      render: (_value: any, row: ProgressDataRow) => {
        if (isGroupRow(row)) return null;
        return (
          <Typography
            variant="body2"
            sx={{
              fontSize: '0.875rem',
              color: '#374151',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: '100%',
            }}
            title={row.delivery_address_line || ''}
          >
            {row.delivery_address_line || '-'}
          </Typography>
        );
      },
    },
    {
      id: 'sales_employee_name',
      label: 'Sale',
      width: '180px',
      render: (_value: any, row: ProgressDataRow) => {
        if (isGroupRow(row)) return null;
        const sale = row.sales_employee;
        if (sale) {
          return (
              <EmployeeDisplay
                name={sale.name || ''}
                nickname={sale.nickname || ''}
                avatar={sale.user?.avatar || null}
                image1={(sale as any).image1 || null}
                employeeId={sale.id}
                userId={sale.user?.id}
              />
          );
        }
        return (
          <Typography variant="body2" sx={{ fontSize: '0.875rem', color: '#374151' }}>
            {row.sales_employee_name || 'N/A'}
          </Typography>
        );
      },
    },
    {
      id: 'employee_names',
      label: 'Nhân viên',
      width: '200px',
      render: (_value: any, row: ProgressDataRow) => {
        if (isGroupRow(row)) return null;

        const employees = row.employees;
        if (employees && employees.length > 0) {
          if (employees.length >= 3) {
            return (
              <EmployeeAvatarGroup
                employees={employees.map((emp) => ({
                  id: emp.id,
                  name: emp.name,
                  nickname: emp.nickname,
                  avatar: emp.user?.avatar || null,
                  image1: (emp as any).image1 || null,
                  userId: emp.user?.id,
                }))}
              />
            );
          }
          return (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '4px 12px' }}>
              {employees.map((emp) => (
                  <EmployeeDisplay
                    key={emp.id}
                    name={emp.name}
                    nickname={emp.nickname}
                    avatar={emp.user?.avatar || null}
                    image1={(emp as any).image1 || null}
                    employeeId={emp.id}
                    userId={emp.user?.id}
                  />
              ))}
            </Box>
          );
        }

        if (!row.employee_names || row.employee_names.length === 0) {
          return (
            <Typography variant="body2" sx={{ fontSize: '0.875rem', color: '#9ca3af' }}>
              -
            </Typography>
          );
        }

        return (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
            {row.employee_names.map((name, index) => (
              <Chip
                key={index}
                label={name}
                size="small"
                sx={{
                  backgroundColor: '#e3f2fd',
                  color: '#1976d2',
                  fontSize: '0.75rem',
                  height: '24px',
                }}
              />
            ))}
          </Box>
        );
      },
    },
    {
      id: 'note',
      label: 'Ghi chú',
      width: '160px',
      render: (_value: any, row: ProgressDataRow) => {
        if (isGroupRow(row)) return null;
        return (
          <Typography
            variant="body2"
            sx={{
              fontSize: '0.875rem',
              color: '#374151',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: '100%',
            }}
            title={row.note || ''}
          >
            {row.note || '-'}
          </Typography>
        );
      },
    },
    {
      id: 'order_total_amount',
      label: 'Doanh Số',
      width: '150px',
      align: 'right' as const,
      headerAlign: 'right' as const,
      render: (_value: any, row: ProgressDataRow) => {
        if (isGroupRow(row)) return null;
        const amount = row.order_total_amount || 0;

        return (
          <Typography
            variant="body2"
            sx={{
              fontSize: '0.875rem',
              color: '#111827',
              fontWeight: 500,
              textAlign: 'right',
            }}
          >
            <NumericFormat
              value={amount}
              displayType="text"
              thousandSeparator="."
              decimalSeparator=","
              decimalScale={0}
              suffix=" đ"
            />
          </Typography>
        );
      },
    },
    {
      id: 'copy',
      label: '',
      width: '50px',
      align: 'center' as const,
      headerAlign: 'center' as const,
      render: (_value: any, row: ProgressDataRow) => {
        if (isGroupRow(row)) return null;

        if (!canCreate) return null;

        return (
          <IconButton
            size="small"
            onClick={(event) => {
              event.stopPropagation();
              if (!onCopyClick) {
                return;
              }
              const { __groupIndex, __dateKey, ...progressRow } = row;
              onCopyClick(progressRow);
            }}
            sx={{
              color: '#6b7280',
              '&:hover': {
                color: '#3b82f6',
                backgroundColor: 'rgba(59, 130, 246, 0.1)',
              },
            }}
          >
            <ContentCopyIcon fontSize="small" />
          </IconButton>
        );
      },
    },
  ];

  if (loading) {
    return (
      <Box
        sx={{
          ...sharedListStyles.container,
          width: '100%',
          maxWidth: '100%',
          minWidth: 0,
          overflow: 'hidden',
          display: 'flex',
          justifyContent: 'center',
          py: 6,
        }}
      >
        <CircularProgress size={32} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        ...sharedListStyles.container,
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,
        overflow: 'hidden',
        boxSizing: 'border-box',
      }}
    >
      {tableRows.length === 0 ? (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body2" sx={{ color: '#9ca3af' }}>
            Không có dữ liệu
          </Typography>
        </Box>
      ) : (
        <>
          <DataTableModule
            data={tableRows}
            columns={columns}
            leftFilterWidth={0}
            reserveLeftFilterSpace={false}
            tableLayout="fixed"
            showCheckbox={false}
            tableContainerSx={{ maxHeight: 'none' }}
            onRowSelect={() => {}}
            onSelectAll={() => {}}
            onRowClick={(row: ProgressDataRow) => {
              if (isGroupRow(row)) return;
              handleRowClick(row);
            }}
            getRowId={(row: ProgressDataRow) => row.id}
            isRowSelected={() => false}
            areAllRowsSelected={false}
            areSomeRowsSelected={false}
            getRowClassName={(row: ProgressDataRow) => (isGroupRow(row) ? 'group-row' : '')}
            enableRowDragDrop={canUpdate}
            onRowReorder={handleRowReorder}
          />
          <AppPagination
            count={totalCount}
            page={currentPage}
            pageSize={pageSize}
            totalPages={totalPages}
            itemLabel="tiến độ"
            onPageChange={(_event, page) => setCurrentPage(page)}
            onPageSizeChange={(nextPageSize) => {
              setPageSize(nextPageSize);
              setCurrentPage(1);
            }}
          />
        </>
      )}

      {!onRowClick && (
      <EditProgressModal
        open={editModalOpen}
        onClose={() => {
          setEditModalOpen(false);
          setSelectedItem(null);
        }}
        item={selectedItem}
        onUpdate={() => {
          if (onUpdate) {
            onUpdate();
          }
        }}
        onSuccess={(message) => {
          if (onSuccess) {
            onSuccess(message);
          }
        }}
        disabled={!canUpdate}
      />
      )}
    </Box>
  );
});

export default MainListOrderProgress;
